/**
 * ProductKpiCards – KPI-Übersicht für /v2/products
 */
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/v2/ui'
import type { ProductKpiSummary } from '@/lib/api/products-kpi'
import { KpiCard, KpiCardSkeleton } from './ProductKpiCard'

// ============================================================================
// Props
// ============================================================================

interface ProductKpiCardsProps {
  kpi: ProductKpiSummary | null
  issuesCount: number
  onFixIssues: () => void
  onAutoGenerate: () => void
}

// ============================================================================
// Main component
// ============================================================================

export function ProductKpiCards({ kpi, issuesCount, onFixIssues, onAutoGenerate }: ProductKpiCardsProps) {
  const { t } = useTranslation()

  if (kpi === null) {
    return (
      <div
        data-testid="kpi-skeleton"
        role="status"
        aria-label={t('productsOverview.kpi.loading')}
        className="grid grid-cols-2 gap-4 md:grid-cols-4"
      >
        <KpiCardSkeleton />
        <KpiCardSkeleton />
        <KpiCardSkeleton />
        <KpiCardSkeleton />
      </div>
    )
  }

  const assortmentPct =
    kpi.without_packshot_count > 0 && kpi.active_count > 0
      ? Math.round((kpi.without_packshot_count / kpi.active_count) * 100)
      : null
  const revenue = kpi.revenue_30d_total.toLocaleString('de-DE', { maximumFractionDigits: 0 })
  const pct = kpi.revenue_delta_pct
  const delta =
    pct !== null
      ? { color: pct >= 0 ? 'text-emerald-600' : 'text-red-500', arrow: pct >= 0 ? '↑' : '↓' }
      : null

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <KpiCard label={t('productsOverview.kpi.active')} value={kpi.active_count} />
      <KpiCard
        label={t('productsOverview.kpi.noPackshot')}
        value={kpi.without_packshot_count}
        sub={
          assortmentPct !== null ? (
            <span className="text-amber-600 dark:text-amber-500">
              {t('productsOverview.kpi.ofAssortment', { pct: assortmentPct })}
            </span>
          ) : undefined
        }
        action={
          kpi.without_packshot_count > 0 ? (
            <Button variant="warning" size="sm" onClick={onAutoGenerate}>
              {t('productsOverview.kpi.autoGenerate')}
            </Button>
          ) : undefined
        }
      />
      <KpiCard
        label={t('productsOverview.kpi.withIssues')}
        value={issuesCount}
        action={
          issuesCount > 0 ? (
            <Button variant="warning" size="sm" onClick={onFixIssues}>
              {t('productsOverview.kpi.fix')}
            </Button>
          ) : undefined
        }
      />
      <KpiCard
        label={t('productsOverview.kpi.revenue30d')}
        value={`${revenue} €`}
        sub={
          delta !== null ? (
            <span className={delta.color}>
              {delta.arrow} {Math.round(Math.abs(pct as number))}% {t('productsOverview.kpi.vsPrev')}
            </span>
          ) : undefined
        }
      />
    </div>
  )
}
